import React, { Component } from 'react';
import { observer } from 'mobx-react';
import {
  StyleSheet,
  View,
  ScrollView,
  BackHandler,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';

import { random } from 'lodash';

import Icon from '../shared/Icon';
import Image from '../shared/Image';

import Text from '../shared/Text';
import Button from '../shared/Button';
import IconButton from '../shared/IconButton';

import appStore from '../../stores/app';
import combatStore, { timeout } from '../../stores/combat';

import heroStore, { quitFromCombat } from '../../stores/hero';

import { FULL_MAX_HEIGHT, FULL_WIDTH_DELTA, HP_OFFSET } from '../common/Body';
import FullBody from '../common/FullBody';

import Timeout from './Timeout';
import Actions, { ACTIONS_WIDTH } from './Actions';
import Chat from '../Chat/index';

import Modal from '../shared/Modal';
import ThingItem from '../common/ThingItem';
import DroppedModal from '../DroppedModal';
import { PaperBackground, getScreenDimension } from '../Island/IslandScreen';

import {
  getDamage,
  getExperience,
  isWin,
  isCombatFinished,
  isWaiting,
  getCombatWarrior,
  getOpponents,
  isWarriorDead,
  hasBonus,
} from '../../utils/combatUtils';

import { t, action } from '../../utils/appUtils';

import { IS_WEB } from '../../lib/appConstants';
import {
  PADDING,
  PADDING1,
  WINDOW_WIDTH,
  MARGIN_5,
  pOrT,
  MARGIN_10,
  MARGIN_15,
  MARGIN_20,
  MARGIN_25,
  BASE_COLOR,
  GOLDEN200,
  MARGIN_3,
} from '../styleConstants';

import WarriorsLine from '../common/WarriorsLine';
import Log from '../common/CombatLog';
import { ChatIconButton, DroppedIconButton } from '../common/Header';

const BG_OFFSET = 40;
const BODY_WIDTH = (WINDOW_WIDTH - BG_OFFSET - ACTIONS_WIDTH - PADDING * 4) / 2;
let BODY_HEIGHT = BODY_WIDTH * FULL_WIDTH_DELTA + HP_OFFSET;
BODY_HEIGHT = Math.min(BODY_HEIGHT, FULL_MAX_HEIGHT);

const INFO_WIDTH = ACTIONS_WIDTH;

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  container: {
    marginBottom: MARGIN_15,
    marginTop: IS_WEB ? MARGIN_15 : pOrT(MARGIN_15, MARGIN_25),
    width: getScreenDimension().width,
  },
  info: {
    position: 'absolute',
    left: '50%',
    top: 0,
    zIndex: 2,
    width: INFO_WIDTH,
    marginLeft: -INFO_WIDTH / 2,
  },
  logContainer: {
    height: pOrT(180, 220),
    // backgroundColor: GREY100,
    marginTop: PADDING1,
    padding: PADDING1,
  },
  actionsWrapper: {
    marginTop: 80,
  },
});

async function init() {
  appStore.toggleLoading(true);

  await combatStore.fetch(heroStore.hero.combat);
  appStore.toggleLoading(false);
}

function getOpponentCombatWarrior(selectedOpponentWarrior: string | null | undefined) {
  const opponents = getOpponents(combatStore.combat, heroStore.hero);
  let opponentCombatWarrior;

  if (opponents) {
    if (selectedOpponentWarrior) {
      opponentCombatWarrior = getCombatWarrior({ warriors: opponents }, selectedOpponentWarrior);
    }
    if (!opponentCombatWarrior) {
      [opponentCombatWarrior] = opponents;
    }
  }

  return opponentCombatWarrior;
}

type State = {
  selectedOpponentWarrior: string | null;
  showDropped: boolean;
};

@observer
export default class CombatScreen extends Component<{}, State> {
  state = {
    selectedOpponentWarrior: null,
    showDropped: false,
  };

  componentDidMount() {
    init();

    BackHandler.addEventListener('hardwareBackPress', this.onBackPress as any);
  }

  componentWillUnmount() {
    BackHandler.removeEventListener('hardwareBackPress', this.onBackPress as any);
  }

  onBackPress = () => {
    BackHandler.exitApp();
  };

  onQuit = async () => {
    appStore.toggleLoading(true);
    await quitFromCombat();
  };

  onNextOpponent = () => {
    const { selectedOpponentWarrior } = this.state;
    const opponents = getOpponents(combatStore.combat, heroStore.hero);

    const opponentIndex = selectedOpponentWarrior
      ? opponents.findIndex((item: any) => selectedOpponentWarrior === item.warrior)
      : 0;
    const nextIndex = opponentIndex + 1 === opponents.length ? 0 : opponentIndex + 1;

    this.setState({
      selectedOpponentWarrior: opponents[nextIndex].warrior,
    });
  };

  render() {
    const { combat } = combatStore;
    const { hero } = heroStore;
    const { selectedOpponentWarrior, showDropped } = this.state;

    if (!combat) return <View style={styles.wrapper} />;

    const opponents = getOpponents(combatStore.combat, heroStore.hero);
    const combatFinished = isCombatFinished(combat);

    const waiting = isWaiting(combat, hero);

    const mainCombatWarrior = getCombatWarrior(combat, heroStore.hero.id);

    const isDead = isWarriorDead(mainCombatWarrior);

    const opponentCombatWarrior = getOpponentCombatWarrior(selectedOpponentWarrior);

    let afterCombatStatus;
    let showDroppedButton;
    if (combatFinished) {
      const iisWin = isWin(combat, hero);
      if (iisWin === 1) {
        afterCombatStatus = t('combat.combatWin');
        showDroppedButton = hasBonus(mainCombatWarrior.bonus);
      } else if (iisWin === -1) {
        afterCombatStatus = t('combat.combatDraw');
      } else {
        afterCombatStatus = t('combat.combatLose');
      }
    }

    return (
      <View style={styles.wrapper}>
        <PaperBackground
          width={getScreenDimension().width}
          height={getScreenDimension().height}
          noPaper
        />
        <ScrollView
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              tintColor={GOLDEN200}
              refreshing={combatStore.fetching}
              onRefresh={() => combatStore.fetch()}
            />
          }
          style={styles.container}
        >
          <View
            style={{
              position: 'relative',
              marginHorizontal: pOrT(MARGIN_20, MARGIN_15),
            }}
          >
            <View style={styles.info}>
              <Image
                style={{
                  width: INFO_WIDTH,
                  height: combatFinished ? 90 : 75,
                  position: 'absolute',
                }}
                resizeMode="stretch"
                source={require('../../assets/images/paper2.png')}
              />
              <View
                style={{
                  alignItems: 'center',
                  paddingHorizontal: PADDING,
                  height: combatFinished ? 90 : 75,
                }}
              >
                {!combatFinished ? (
                  <>
                    <Text bold>
                      {t('combat.damage')} {getDamage(combat, hero)}
                    </Text>
                    {waiting ? <Text>{t('combat.waitingOpponentStrike')}</Text> : null}
                    {isDead ? <Text>{t('combat.waitingCombatEnds')}</Text> : null}
                    <Timeout
                      /* hideTimer={waiting || isDead} */
                      combat={combat}
                      onFinished={timeout}
                    />
                    {IS_WEB && (waiting || isDead) ? (
                      <>
                        <IconButton
                          onPress={() =>
                            action(() => {
                              combatStore.fetch();
                            })
                          }
                          style={{ marginTop: MARGIN_5 }}
                          size={14}
                          source={require('../../assets/images/refresh.png')}
                          iconStyle={{ width: 26, height: 26 }}
                          title={t('refresh')}
                          name="refresh"
                        />
                        {appStore.smallLoading ? (
                          <ActivityIndicator color={BASE_COLOR} style={{ marginTop: MARGIN_5 }} />
                        ) : null}
                      </>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Text bold>
                      {t('combat.fightFinished')} {afterCombatStatus}
                    </Text>
                    <Text bold>
                      {t('combat.damage')} {getDamage(combat, hero)} {t('combat.experience')}{' '}
                      {getExperience(combat, hero)}
                    </Text>
                  </>
                )}
                <View style={{ alignItems: 'center', flexDirection: 'row', marginTop: MARGIN_5 }}>
                  {showDroppedButton ? (
                    <DroppedIconButton
                      style={undefined}
                      onPress={() => this.setState({ showDropped: true })}
                    />
                  ) : null}
                  <ChatIconButton
                    style={showDroppedButton ? { marginLeft: MARGIN_10 } : undefined}
                  />
                </View>
              </View>
              {combatFinished ? (
                <View style={{ alignItems: 'center', marginTop: MARGIN_10 }}>
                  <Button onPress={this.onQuit}>{t('combat.quit')}</Button>
                </View>
              ) : null}
            </View>
            <View>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <View>
                  <FullBody inCombat height={BODY_HEIGHT} warrior={heroStore.hero} applyEnabled />
                </View>
                {!combatFinished && !waiting && !isDead ? (
                  <>
                    <View style={styles.actionsWrapper}>
                      <Actions
                        onNextOpponent={opponents.length > 1 ? this.onNextOpponent : null}
                        opponentCombatWarriorId={opponentCombatWarrior!.warrior}
                      />
                    </View>
                    <View style={{ zIndex: 3 }}>
                      <FullBody
                        key={opponentCombatWarrior!.warrior}
                        inCombat
                        height={BODY_HEIGHT}
                        warrior={opponentCombatWarrior!._warrior}
                        showInfo
                      />
                    </View>
                  </>
                ) : (
                  <Icon
                    style={{
                      marginTop: 0,
                      marginRight: 40,
                      width: 175,
                      height: 350,
                    }}
                    size={200}
                    source={
                      (
                        {
                          0: require('../../assets/images/silhouette0.png'),
                          1: require('../../assets/images/silhouette1.png'),
                          2: require('../../assets/images/silhouette2.png'),
                        } as any
                      )[random(0, 2)]
                    }
                    name={waiting || (isDead && !combatFinished) ? 'combatWaiting' : 'combatCover'}
                  />
                )}
              </View>

              <View style={{ marginTop: PADDING1 }}>
                <Image
                  style={{
                    width: getScreenDimension().width - 40,
                    height: pOrT(190, 220),
                    position: 'absolute',
                  }}
                  resizeMode="stretch"
                  source={require('../../assets/images/paper2.png')}
                />
                <View style={styles.logContainer}>
                  <View
                    style={{
                      alignItems: 'center',
                    }}
                  >
                    <WarriorsLine showHp combat={combat} />
                  </View>
                  <View style={{ flex: 1, paddingHorizontal: MARGIN_10 }}>
                    <Log combat={combat} />
                  </View>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
        <Chat id="combat" onHide={() => appStore.toggleChat(false)} visible={appStore.chat} />
        {showDropped ? (
          <DroppedModal
            bonus={mainCombatWarrior.bonus!}
            onHide={() => this.setState({ showDropped: false })}
          />
        ) : null}
      </View>
    );
  }
}
